import { formatCode } from "@/clinical-codes/codeUtils";
import type { OPCS4Code } from "@/clinical-codes/types/ClinicalCodeTypes";
import type { OpcsVersion } from "@/clinical-codes/opcsVersion";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export type UseSearchOpcs4CodesResult = {
  codes: OPCS4Code[] | null;
  getCodeByValue: (code: string) => Promise<OPCS4Code | null>;
  isLoading: boolean;
  error: Error | null;
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
};

export type { OPCS4Code };

const PAGE_SIZE = 100; // Increased from 50 to 100 for better performance

export const useSearchOpcs4Codes = (
  searchQuery: string = "",
  codeId?: string,
  version?: OpcsVersion,
): UseSearchOpcs4CodesResult => {
  const {
    data: codesData,
    error: codesError,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["opcs4-codes", searchQuery, codeId, version],
    queryFn: async ({ pageParam }) => {
      let baseQuery = supabase.from("opcs4_codes").select("*", { count: "exact" });

      if (codeId && !searchQuery) {
        // Only utilize codeId if it is provided and searchQuery is not provided
        baseQuery = baseQuery.eq("id", codeId);
      }

      if (searchQuery) {
        const formattedCode = formatCode(searchQuery);
        baseQuery = baseQuery.or(`code.ilike.%${formattedCode}%,description.ilike.%${searchQuery}%`);
      }

      if (version) {
        baseQuery = baseQuery.eq("version", version);
      }

      const start = Number(pageParam) * PAGE_SIZE;
      const query = baseQuery.order("code", { ascending: true }).range(start, start + PAGE_SIZE - 1);

      const { data, error, count } = await query;

      if (error) {
        console.error(`Error fetching clinical codes:`, error);
        throw error;
      }

      return {
        codes: data.map((code) => ({
          id: code.id,
          code: code.code.trim().toUpperCase(),
          description: code.description,
        })),
        pageParam: Number(pageParam),
        count: count || 0,
      };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.count) return undefined;
      const nextPage = lastPage.pageParam + 1;
      const totalPages = Math.ceil(lastPage.count / PAGE_SIZE);
      return nextPage < totalPages ? nextPage : undefined;
    },
    initialPageParam: 0,
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    meta: {
      errorMessage: "Failed to load clinical codes",
    },
  });

  // Cache for all loaded codes
  const codeCache = useMemo(() => {
    if (!codesData?.pages) return new Map<string, OPCS4Code>();
    return new Map(codesData.pages.flatMap((page) => page.codes).map((code) => [`${code.code}`, code]));
  }, [codesData?.pages]);

  // Function to get a specific code by value and type
  const getCodeByValue = async (codeValue: string) => {
    // First, standardize the format of the code
    const standardizedCode = formatCode(codeValue);

    // Check the cache using combined key
    const cacheKey = `${standardizedCode}`;
    const cachedCode = codeCache.get(cacheKey);
    if (cachedCode) {
      console.log("Found code in cache:", cachedCode);
      return cachedCode;
    }

    let codeQuery = supabase.from("opcs4_codes").select("*").eq("code", standardizedCode);
    if (version) {
      codeQuery = codeQuery.eq("version", version);
    }
    const { data, error } = await codeQuery.maybeSingle();

    if (error) {
      console.error("Error fetching specific code:", error);
      return null;
    }

    if (!data) {
      console.log("No code found in database");
      return null;
    }

    return data;
  };

  if (codesError) {
    console.error("Query error:", codesError);
    toast.error("Failed to load clinical codes. Please refresh the page.");
  }

  const fetchNext = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  };

  return {
    codes: codesData?.pages.flatMap((page) => page.codes) ?? [],
    getCodeByValue,
    isLoading,
    error: codesError,
    fetchNextPage: fetchNext,
    hasNextPage: hasNextPage ?? false,
  };
};
